import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  School,
  ArrowRight,
  Check,
} from 'lucide-angular';
import { WizardDataService } from '../../../../common/wizard/wizard-data.service';

@Component({
  selector: 'app-step-school',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './step-school.html',
  styleUrl: './step-school.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepSchoolV2Component implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly wizardDataService = inject(WizardDataService);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);

  protected readonly schoolForm: FormGroup;

  protected readonly icons = {
    School,
    ArrowRight,
    Check,
  };

  protected readonly nextStepUrl = '/wizard/v2/personalize';

  constructor() {
    this.schoolForm = this.fb.group({
      school: [''],
      district: [''],
      state: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    // Load data from backend first
    const existingData = await this.wizardDataService.ensureLoaded();

    // Pre-populate form with loaded data
    if (existingData.school || existingData.district || existingData.state) {
      this.schoolForm.patchValue({
        school: existingData.school || '',
        district: existingData.district || '',
        state: existingData.state || '',
      }, { emitEvent: false });
    }

    // Auto-save on form changes (after form is populated)
    this.schoolForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.wizardDataService.save({
          school: value.school,
          district: value.district,
          state: value.state,
        });
      });
  }

  protected setNoSchool(): void {
    this.schoolForm.patchValue({ school: 'Independent Educator' });
  }

  protected onSubmit(): void {
    if (this.schoolForm.invalid) {
      this.schoolForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);

    this.isLoading$.next(false);
    this.router.navigate([this.nextStepUrl]);
  }
}

